import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import './ToolPage.css'
import './WebhookTester.css'
import { fetchWebhookRequests, webhookStreamUrl, webhookCaptureUrl } from './utils/apiClient'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const WebhookTesterView = () => {
  useDocumentMeta({
    title: 'Webhook Tester, acompanhando requisições | jarder.dev',
    description:
      'Veja em tempo real as requisições HTTP que chegam no seu endpoint de teste.',
  })

  const { id } = useParams()
  const [requests, setRequests] = useState([])
  const [status, setStatus] = useState('loading')
  const [expandedId, setExpandedId] = useState(null)
  const [copied, setCopied] = useState(false)

  const captureUrl = webhookCaptureUrl(id)

  useEffect(() => {
    let cancelled = false

    fetchWebhookRequests(id)
      .then((data) => {
        if (cancelled) return
        setRequests(data.requests)
        setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) setStatus('notfound')
      })

    const eventSource = new EventSource(webhookStreamUrl(id))
    eventSource.onmessage = (event) => {
      const parsed = JSON.parse(event.data)
      setRequests((current) => [parsed, ...current])
    }

    return () => {
      cancelled = true
      eventSource.close()
    }
  }, [id])

  const copyUrl = () => {
    navigator.clipboard.writeText(captureUrl)
    setCopied(true)
    trackEvent('tool_copy', { tool: 'webhook_tester' })
  }

  const toggleExpanded = (requestId) => {
    setExpandedId((current) => (current === requestId ? null : requestId))
  }

  if (status === 'loading') {
    return (
      <div className="tool-page">
        <p>Carregando...</p>
      </div>
    )
  }

  if (status === 'notfound') {
    return (
      <div className="tool-page">
        <Link to="/ferramentas" className="tool-back-link">
          ← Todas as ferramentas
        </Link>
        <h1>Endpoint não encontrado</h1>
        <p className="tool-disclaimer">
          Esse endpoint não existe ou expirou por falta de uso.
        </p>
        <Link to="/ferramentas/webhook-tester" className="tool-button">
          Criar um novo endpoint
        </Link>
      </div>
    )
  }

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Webhook Tester</h1>
      <p className="tool-disclaimer">
        Mande qualquer requisição HTTP pra URL abaixo: ela aparece na lista
        automaticamente, sem precisar atualizar a página. Não mande
        segredos reais (chaves de API, senhas), e evite compartilhar essa
        URL: quem tiver ela também consegue ver o que foi capturado aqui.
      </p>

      <div className="tool-result-card">
        <span className="tool-result-label">Sua URL de teste</span>
        <span className="tool-result-value" style={{ wordBreak: 'break-all' }}>
          {captureUrl}
        </span>
        <div className="tool-result-actions">
          <button type="button" className="tool-button tool-button-secondary" onClick={copyUrl}>
            {copied ? 'Copiado!' : 'Copiar'}
          </button>
        </div>
      </div>

      <h2 className="webhook-requests-heading">
        Requisições recebidas ({requests.length})
      </h2>

      {requests.length === 0 ? (
        <p className="tool-import-hint">
          Nenhuma requisição ainda. Mande uma pra URL acima e ela aparece
          aqui na hora.
        </p>
      ) : (
        <ul className="webhook-requests-list">
          {requests.map((request) => (
            <li key={request.id} className="webhook-request-item">
              <button
                type="button"
                className="webhook-request-summary"
                onClick={() => toggleExpanded(request.id)}
              >
                <span className={`webhook-method webhook-method-${request.method.toLowerCase()}`}>
                  {request.method}
                </span>
                <span className="webhook-path">{request.path}</span>
                <span className="webhook-time">
                  {new Date(request.receivedAt).toLocaleTimeString('pt-BR')}
                </span>
              </button>

              {expandedId === request.id && (
                <div className="webhook-request-detail">
                  <h3>Headers</h3>
                  <pre>{JSON.stringify(request.headers, null, 2)}</pre>
                  {request.body && (
                    <>
                      <h3>Body</h3>
                      <pre>{request.body}</pre>
                    </>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default WebhookTesterView
